import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import Select from 'react-select'
import LoxSelect from '../../../components/LoxSelect'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import Loading from '../../../components/Loading'
import { DateFormat, CurrencyFormat, PercentFormat, buildQueryString, getTodayArizona, formatApr } from '../../../helpers/formatting'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { selectAuth } from "../../../redux/authSlice"
import LayoutSidebar from '../../../components/LayoutSidebar'
import LoanAgreement from './LoanAgreement';
import Instruction from '../../../components/elements/Instruction'
import ManagerApprovalStatus from './ManagerApprovalStatus'
import CashOutRefinanceFields from './components/CashOutRefinanceFields'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import CollectionRefinanceFields from './components/CollectionRefinanceFields';
import CollectionRefinanceSchedule from './components/CollectionRefinanceSchedule';
import { format } from 'date-fns';
import { set } from 'lodash';

export default function LoanRefinance() {
  const auth = useSelector(selectAuth)
  const dispatch = useDispatch();
  const { handleSubmit, control, watch, setValue, getValues, formState: { errors } } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const today = getTodayArizona();
  const nextDay = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const [refinanceInfo, setRefinanceInfo] = useState(null)
  const [loanId, setLoanId] = useState(null)
  const params = useParams()
  const [searchParams] = useSearchParams()
  const [scheduleData, setScheduleData] = useState(null)
  const [submissionType, setSubmissionType] = useState('patch');
  const [newLoanAmount, setNewLoanAmount] = useState(0);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const watchPrincipalDiscount = watch('principal_discount')
  const watchInterestDiscount = watch('interest_discount')
  const watchAdditionalFunds = watch('additional_funds')
  const watchPayFrequencyDropdownOptionId = watch('pay_frequency_dropdown_option_id')
  const watchProgramId = watch('program_id')
  const watchApr = watch('apr')
  const watchIsPayBeforeHoliday = watch('is_pay_before_holiday')
  const watchLoanFundingMethod = watch('loan_funding_method_dropdown_option_id')
  const watchDesiredAmount = watch('desired_amount')
  const watchNextPayDate = watch('next_pay_date')
  const [isCashOut, setIsCashOut] = useState(false)
  const [isCollectionRefinance, setIsCollectionRefinance] = useState(false)
  const [minPrincipal, setMinPrincipal] = useState(null)
  const [maxPrincipal, setMaxPrincipal] = useState(null)
  const [minApr, setMinApr] = useState(null)
  const [maxApr, setMaxApr] = useState(null)
  const [numOfInstallments, setNumOfInstallments] = useState(null)
  const customerId = params?.id
  const [refinanceRequestInfo, setRefinanceRequestInfo] = useState({})
  const [managerApprovalStatusResponse, setManagerApprovalStatusResponse] = useState({})
  const [managerApprovalStatusInfo, setManagerApprovalStatusInfo] = useState({})
  const [currentStep, setCurrentStep] = useState(null)
  const [loanAgreementData, setLoanAgreementData] = useState(null);
  const [refinanceErrorMessage, setRefinanceErrorMessage] = useState('')
  const [requiresManagerApproval, setRequiresManagerApproval] = useState(false)
  const [payFrequencyValue, setPayFrequencyValue] = useState("");
  const [initialPayFrequency, setInitialPayFrequency] = useState("");
  const [initialPayFrequencyOption, setInitialPayFrequencyOption] = useState("");
  const [isUpdatingTerms, setIsUpdatingTerms] = useState(false);
  const [termsAgreement, setTermsAgreement] = useState(false);
  const isCashOutParam = searchParams.get('is_cash_out');

  // collection refinance variables
  const [collectionRefiMinMax, setCollectionRefiMinMax] = useState({});
  const [collectionRefiRates, setCollectionRefiRates] = useState([]);
  const isCollectionRefinanceParam = searchParams.get('is_collections_refinance');
  const [offerSelection, setOfferSelection] = useState(null);
  const [isMaxMinProcessing, setIsMaxMinProcessing] = useState(false);
  const [isSendingForSignature, setIsSendingForSignature] = useState(false);
  const [collectionsFirstPaymentDate, setCollectionsFirstPaymentDate] = useState(null);
  const [disableUpdateOfferButton, setDisableUpdateOfferButton] = useState(false);
  const [displayFrequencyWarning, setDisplayFrequencyWarning] = useState(false);

  // Track form values when schedule was generated
  const [scheduleFormValues, setScheduleFormValues] = useState({});

  // Function to check if form has changes since schedule was generated
  const hasFormChanges = () => {
    if (Object.keys(scheduleFormValues).length === 0) return false;

    const currentValues = {
      principal_discount: watchPrincipalDiscount ?? 0,
      interest_discount: watchInterestDiscount ?? 0,
      additional_funds: watchAdditionalFunds ?? 0,
      pay_frequency_dropdown_option_id: watchPayFrequencyDropdownOptionId?.value,
      program_id: watchProgramId?.value,
      apr: watchApr ?? 0,
      is_pay_before_holiday: watchIsPayBeforeHoliday?.value,
      pay_first_day_of_week_dropdown_option_id: watch('pay_first_day_of_week_dropdown_option_id')?.value,
      pay_second_day_of_week_dropdown_option_id: watch('pay_second_day_of_week_dropdown_option_id')?.value,
      pay_first_week_of_month_dropdown_option_id: watch('pay_first_week_of_month_dropdown_option_id')?.value,
      pay_second_week_of_month_dropdown_option_id: watch('pay_second_day_of_week_dropdown_option_id')?.value,
      pay_first_day_of_month_dropdown_option_id: watch('pay_first_day_of_month_dropdown_option_id')?.value,
      pay_second_day_of_month_dropdown_option_id: watch('pay_second_day_of_month_dropdown_option_id')?.value,
      next_pay_date: watch('next_pay_date') ?? null,
      semi_monthly_pay_frequency: watch('semi_monthly_pay_frequency')?.value,
      monthly_pay_frequency: watch('monthly_pay_frequency')?.value,
    };

    const hasChanges = Object.keys(currentValues).some((key) => {
      if (key === 'pay_frequency_dropdown_option_id' || key === 'program_id' || key === 'is_pay_before_holiday') {
        return currentValues[key] !== scheduleFormValues[key];
      }
      if (key === 'apr') {
        return Math.abs(parseFloat(currentValues[key]) - parseFloat(scheduleFormValues[key])) > 0.01;
      }
      if (key === 'principal_discount' || key === 'interest_discount' || key === 'additional_funds') {
        return Math.abs(parseFloat(currentValues[key]) - parseFloat(scheduleFormValues[key])) > 0.01;
      }
      return currentValues[key] !== scheduleFormValues[key];
    });

    return hasChanges;
  };

  const navi = useNavigate()
  const totalSteps = isCashOut ? 3 : 4;
  const stepDetails = [
    {
      title: isCollectionRefinance ? 'STEP 1 - Customer Input' : `STEP 1 of ${totalSteps} - Negotiate`,
      description: 'This will roll over your existing outstanding balance into a new loan with new terms and a new loan agreement.'
    },
    {
      title: isCollectionRefinance ? 'STEP 2 - Negotiate' : `STEP 2 of ${totalSteps} - Validate`,
      description: 'This will roll over your existing outstanding balance into a new loan with new terms and a new loan agreement.'
    },
    {
      title: 'STEP 3 of 4 - Manager Approval',
      description: 'This loan has been sent for manager approval. Once received you will be able to continue.'
    },
    {
      title: isCollectionRefinance ? 'STEP 3 - Customer Signature' : `STEP 4 of ${totalSteps} - Customer Signature`,
      description: 'Customer must sign for this new loan just like any other loan. Once their signature is completed, it will proceed for tribal approval like always.'
    },
  ]

  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0]
  const payFrequencyOptions = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key != 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.id }))

  const dayOfWeek = dropdowns.data.filter((o) => o.code_key === 'day_of_week')[0]
  const weekendsCodeKeys = ['day_of_week_saturday', 'day_of_week_sunday']
  const dayOfWeekOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfWeek.id && !weekendsCodeKeys.includes(o.code_key)).map((o) => ({ label: o.name, value: o.id }))

  const weekOfMonth = dropdowns.data.filter((o) => o.code_key === 'week_of_month')[0]
  const weekOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === weekOfMonth.id).map((o) => ({ label: o.name, value: o.id }))

  const dayOfMonth = dropdowns.data.filter((o) => o.code_key === 'day_of_month')[0]
  const dayOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfMonth.id).map((o) => ({ label: o.name, value: o.id }))

  const [payFrequencyValueOption, setPayFrequencyValueOption] = useState("")

  const refinanceReason = dropdowns.data.filter((o) => o.code_key === 'refinance_reason')[0];
  const refinanceReasonOptions = dropdownOptions.filter((o) => o.dropdown_id === refinanceReason.id && o.is_active === 1).map((o) => ({ label: o.name, value: o.id }));

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      setCurrentStep(0)
    }
  }, [auth.accessToken])

  useEffect(() => {
    let timeoutId;

    if (currentStep === 0) {
      setScheduleData(null)
      setScheduleFormValues({})
      setManagerApprovalStatusResponse({})
      setManagerApprovalStatusInfo({})
      // Clear APR and program-specific limits when returning to step 0
      resetProgramInfo();
      setValue('apr', null);
      // Only call getRefinanceInfo if we're not in the middle of updating terms
      if (!isUpdatingTerms) {
        setRefinanceInfo(null)
        getRefinanceInfo(customerId)
      }
    } else if (currentStep === 2 && managerApprovalStatusInfo?.code_key != "mgr_approval_status_approved" && managerApprovalStatusInfo?.code_key != "mgr_approval_status_declined") {
      getManagerApprovalStatus();
      const startLoop = () => {
        timeoutId = setTimeout(() => {
          getManagerApprovalStatus();
          startLoop();
        }, 15000);
      };
      startLoop();
    } else if (currentStep === 3) {
      getLoanAgreementData();
    }

    if (currentStep != 2) {
      clearTimeout(timeoutId);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentStep]);

  useEffect(() => {
    if (!refinanceInfo?.payoff_amount) return;

    const additionalFunds = watchAdditionalFunds ?? 0;
    const principalDiscount = watchPrincipalDiscount ?? 0;
    const interestDiscount = watchInterestDiscount ?? 0;

    if (isCashOut) {
      setNewLoanAmount(+refinanceInfo.payoff_amount + +additionalFunds - +principalDiscount - +interestDiscount)
    } else {
      setNewLoanAmount(+refinanceInfo.payoff_amount - +principalDiscount - +interestDiscount)
    }
  }, [watchPrincipalDiscount, watchInterestDiscount, watchAdditionalFunds, refinanceInfo?.payoff_amount, isCashOut])

  useEffect(() => {
    if (watchPayFrequencyDropdownOptionId != "") {
      // Only reset program if we're not updating terms
      if (!isUpdatingTerms) {
        setValue('program_id', "")
      }

      const filteredPrograms = refinanceInfo?.programs?.filter(
        (o) => o.pay_frequency_dropdown_option_id == watchPayFrequencyDropdownOptionId?.value
      );

      // Only for no cashout
      if (!isCashOutParam && !isCollectionRefinanceParam) {
        // reset semi-monthly and monthly options when pay frequency changes
        setValue('semi_monthly_pay_frequency', "");
        setValue('monthly_pay_frequency', "");
        setPayFrequencyValueOption("");
      }

      setAvailablePrograms(filteredPrograms);
      // Only reset program info if we're not updating terms
      if (!isUpdatingTerms) {
        resetProgramInfo();
      }
    }
  }, [watchPayFrequencyDropdownOptionId])

  // Update getRefinanceInfo to accept a body parameter
  const getRefinanceInfo = async (customerId, overrideBody = null) => {
    let endpoint = `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/refinance_info`;
    // Prepare body for POST request
    let body = {};
    if (overrideBody) {
      body = overrideBody;
    }

    const isCashOutParam = searchParams.get('is_cash_out');
    if (isCashOutParam) {
      body.is_cash_out = isCashOutParam;
      setIsCashOut(true);
    } else if (isCollectionRefinanceParam) {
      body.is_collections_refinance = isCollectionRefinanceParam;
      setIsCollectionRefinance(true);
    }
    request.setEndpoint(endpoint)
    try {
      const response = await request.post(body)
      if (response.status === 200 || response.status === 201) {
        setRefinanceInfo(response?.data?.data)
        setLoanId(response?.data?.data?.active_loan?.id)

        if (isUpdatingTerms) {
          const updatedPrograms = updateRefinanceTermsOnly(response?.data?.data)
          setAvailablePrograms(updatedPrograms)
        } else {
          resetFormWithRefinanceInfo(response?.data?.data)
          setAvailablePrograms(response?.data?.data?.programs)
          setNewLoanAmount(+response?.data?.data?.payoff_amount)
        }

        setRefinanceErrorMessage("")

        if (isCollectionRefinanceParam && response?.data?.data?.collection_refi_lead_id) {
          getCollectionRefiMinMax(response?.data?.data?.active_loan?.id, response?.data?.data?.customer?.pay_frequency_dropdown_option_id, response?.data?.data?.min_first_payment_date)
        }
      } else {
        setRefinanceInfo([]);
        setRefinanceErrorMessage("")
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setRefinanceInfo([])
      if (error?.response?.status == 412) {
        setRefinanceErrorMessage(error?.response?.data?.message)
      } else {
        setRefinanceErrorMessage("")
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  }

  const getCollectionRefiMinMax = async (loanId, payFrequencyDropdownOptionId = null, firstPaymentDate = null) => {
    if (firstPaymentDate == null) {
      firstPaymentDate = collectionsFirstPaymentDate ? format(collectionsFirstPaymentDate, 'yyyy-MM-dd') : refinanceInfo?.min_first_payment_date;
    }

    if (payFrequencyDropdownOptionId == null) {
      payFrequencyDropdownOptionId = watchPayFrequencyDropdownOptionId?.value
    }

    let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/collections_refi_min_max?pay_frequency_dropdown_option_id=` + payFrequencyDropdownOptionId + '&first_payment_date=' + firstPaymentDate;

    request.setEndpoint(endpoint)
    try {
      setIsUpdatingTerms(true);
      setIsMaxMinProcessing(true);
      const response = await request.get(params)
      if (response.status === 200 || response.status === 201) {
        setIsMaxMinProcessing(false);
        setCollectionRefiMinMax(response?.data?.data)
        if (!response?.data?.data) {
          setDisplayFrequencyWarning(true);
          setDisableUpdateOfferButton(true);
        }
      } else {
        setIsMaxMinProcessing(false);
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setCollectionRefiMinMax({})
      }
      setCollectionRefiRates([])
      setScheduleData(null) // hide the loaded schedule
      setOfferSelection(null)
      setCurrentStep(0)
    } catch (error) {
      setCollectionRefiMinMax({})
      setCollectionRefiRates([])
      setScheduleData(null) // // hide the loaded schedule
      setOfferSelection(null)
      setIsMaxMinProcessing(false);
      setCurrentStep(0)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const resetProgramInfo = () => {
    setMinApr(null)
    setMaxApr(null)
    setMaxPrincipal(null)
    setMinPrincipal(null)
    setNumOfInstallments(null)
  }

  const isFormValid = () => {
    const hasPayFrequency = watchPayFrequencyDropdownOptionId?.value;
    const hasProgram = watchProgramId?.value;
    const aprValue = Number(watchApr);
    // If minApr or maxApr are null, skip APR range validation but still require a positive value
    const hasApr = aprValue && aprValue > 0 && (minApr === null || aprValue >= minApr) && (maxApr === null || aprValue <= maxApr);
    const hasIsPayBeforeHoliday = watchIsPayBeforeHoliday?.value !== undefined && watchIsPayBeforeHoliday?.value !== null;
    let hasPayFrequencyDependentFields = true;
    // When pay frequency is semi-monthly or monthly, require the sub-option to be selected
    if ((payFrequencyValue === 'Semi-Monthly' || payFrequencyValue === 'Monthly') && !payFrequencyValueOption) {
      hasPayFrequencyDependentFields = false;
    } else if (payFrequencyValueOption === 'semimonthly-option1') {
      hasPayFrequencyDependentFields = !!watch('pay_first_day_of_month_dropdown_option_id')?.value &&
        !!watch('pay_second_day_of_month_dropdown_option_id')?.value;
    } else if (payFrequencyValueOption === 'semimonthly-option2') {
      hasPayFrequencyDependentFields = !!watch('pay_first_week_of_month_dropdown_option_id')?.value &&
        !!watch('pay_first_day_of_week_dropdown_option_id')?.value &&
        !!watch('pay_second_week_of_month_dropdown_option_id')?.value &&
        !!watch('pay_second_day_of_week_dropdown_option_id')?.value;
    } else if (payFrequencyValueOption === 'monthly-option1') {
      hasPayFrequencyDependentFields = !!watch('pay_first_day_of_month_dropdown_option_id')?.value;
    } else if (payFrequencyValueOption === 'monthly-option2') {
      hasPayFrequencyDependentFields = !!watch('pay_first_week_of_month_dropdown_option_id')?.value &&
        !!watch('pay_first_day_of_week_dropdown_option_id')?.value;
    } else if (payFrequencyValueOption === 'weekly-default' || payFrequencyValueOption === 'biweekly-default') {
      // Weekly and bi-weekly don't have dependent fields to validate
      hasPayFrequencyDependentFields = true;
    }

    // For cash-out refinance, validate additional funds
    let hasAdditionalFunds = true;
    if (isCashOut) {
      const terms = refinanceInfo?.terms || {};
      const minCashout = parseFloat(terms.min_additional_principal_amount || refinanceInfo?.min_additional_funds || 0);
      const maxCashout = parseFloat(terms.max_additional_principal_amount || refinanceInfo?.max_additional_funds || 0);
      hasAdditionalFunds = watchAdditionalFunds && watchAdditionalFunds > 0 && watchAdditionalFunds >= minCashout && watchAdditionalFunds <= maxCashout;
    }

    const result = hasPayFrequency && hasProgram && hasApr && hasIsPayBeforeHoliday && hasAdditionalFunds && hasPayFrequencyDependentFields;
    return result;
  }

  const handleProgramChange = (field, val) => {
    field.onChange({
      label: val?.label,
      value: val?.value,
      pay_frequency_dropdown_option_id: val?.pay_frequency_dropdown_option_id,
    })

    if (val?.value) {
      const program = availablePrograms.find((p) => p.id === val.value)
      if (program) {
        // Use APR range provided by backend (already capped by last non-collections refi APR
        // and floored at program min_apr), falling back to program's own min/max if needed.
        const programAprMin = program.apr_min ?? program.min_apr ?? null;
        const programAprMax = program.apr_max ?? program.max_apr ?? null;

        setMinApr(programAprMin);
        setMaxApr(programAprMax);
        // When a program is chosen and no APR has been entered yet, prefill with the max cap.
        if (!watchApr && programAprMax !== null && programAprMax !== undefined) {
          setValue('apr', programAprMax);
        }
        setMaxPrincipal(program.max_principal)
        setMinPrincipal(program.min_principal)
        setNumOfInstallments(program.number_of_installments)
      }
    } else {
      resetProgramInfo();
    }
  };

  // Function to update only the terms-related fields without resetting the entire form
  const updateRefinanceTermsOnly = (data) => {
    // Update available programs
    setAvailablePrograms(data?.programs)

    // If there's a currently selected program, update its limits
    const currentProgramId = getValues('program_id')?.value;
    if (currentProgramId) {
      const program = data?.programs?.find((p) => p.id === currentProgramId);
      if (program) {
        // Prefer backend-provided APR range when available; it already accounts
        // for last non-collections refi APR, program caps, and program min_apr.
        const programAprMin = program.apr_min ?? program.min_apr ?? null;
        const programAprMax = program.apr_max ?? program.max_apr ?? null;

        setMinApr(programAprMin);
        setMaxApr(programAprMax);
        setMaxPrincipal(program.max_principal);
        setMinPrincipal(program.min_principal);
        setNumOfInstallments(program.number_of_installments);
      }
    } else {
      // No program selected; clear APR bounds until a program is chosen.
      setMinApr(null);
      setMaxApr(null);
    }
    // Return the updated programs for use in restoration logic
    return data?.programs;
  }

  const resetFormWithRefinanceInfo = (data) => {
    setValue('is_pay_before_holiday', [{ label: 'Before', value: 1 }, { label: 'After', value: 0 }].find(
      (option) => option.value == data.customer.is_pay_before_holiday
    ) ?? '')

    const val = payFrequencyOptions.find(
      (option) => option.value == data.customer.pay_frequency_dropdown_option_id
    )
    setValue('pay_frequency_dropdown_option_id', val ?? '')
    setPayFrequencyValue(val?.label)
    setInitialPayFrequency(val?.label || "");

    // APR bounds are determined once a program is selected; start with no bounds.
    setMinApr(null);
    setMaxApr(null);

    // (Optionally keep setting min/max principal, installments, etc. from program if needed, but not APR)

    setValue('pay_first_day_of_week_dropdown_option_id', dayOfWeekOptions.find(
      (option) => option.value == data.customer.pay_first_day_of_week_dropdown_option_id
    ) ?? '')

    setValue('next_pay_date', data.customer.next_pay_date || nextDay)
    // setValue('next_pay_date', null);

    // if pay frequency is semi-monthly and specific days
    if (data.customer.pay_frequency_dropdown_option_id == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_semimonthly')?.id
      && data.customer.pay_first_day_of_month_dropdown_option_id != ""
      && data.customer.pay_second_day_of_month_dropdown_option_id != ""
      && data.customer.pay_first_day_of_month_dropdown_option_id != null
      && data.customer.pay_second_day_of_month_dropdown_option_id != null) {
      setPayFrequencyValueOption('semimonthly-option1')
      setInitialPayFrequencyOption('semimonthly-option1');
      setValue('semi_monthly_pay_frequency', [{ label: 'Specific Days', value: 'semimonthly-option1' }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }].find(
        (option) => option.value == 'semimonthly-option1'
      ) ?? '')
    }

    // if pay frequency is semi-monthly and week day and week of month
    if (data.customer.pay_frequency_dropdown_option_id == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_semimonthly')?.id
      && data.customer.pay_first_week_of_month_dropdown_option_id != ""
      && data.customer.pay_first_day_of_week_dropdown_option_id != ""
      && data.customer.pay_second_week_of_month_dropdown_option_id != ""
      && data.customer.pay_second_day_of_week_dropdown_option_id != ""
      && data.customer.pay_first_week_of_month_dropdown_option_id != null
      && data.customer.pay_first_day_of_week_dropdown_option_id != null
      && data.customer.pay_second_week_of_month_dropdown_option_id != null
      && data.customer.pay_second_day_of_week_dropdown_option_id != null) {
      setPayFrequencyValueOption('semimonthly-option2')
      setInitialPayFrequencyOption('semimonthly-option2');
      setValue('semi_monthly_pay_frequency', [{ label: 'Specific Days', value: 'semimonthly-option1' }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }].find(
        (option) => option.value == 'semimonthly-option2'
      ) ?? '')
    }

    // if pay frequency is monthly and Pay Day of Months
    if (data.customer.pay_frequency_dropdown_option_id != ""
      && data.customer.pay_frequency_dropdown_option_id == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_monthly')?.id
      && data.customer.pay_first_day_of_month_dropdown_option_id != ""
      && data.customer.pay_first_day_of_month_dropdown_option_id != null) {
      setPayFrequencyValueOption('monthly-option1')
      setInitialPayFrequencyOption('monthly-option1');
      setValue('monthly_pay_frequency', [{ label: 'Pay Day of Month', value: 'monthly-option1' }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }].find(
        (option) => option.value == 'monthly-option1'
      ) ?? '')
    }

    // if pay frequency is monthly and week day and week of month
    if (data.customer.pay_frequency_dropdown_option_id != ""
      && data.customer.pay_frequency_dropdown_option_id == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_monthly')?.id
      && data.customer.pay_first_week_of_month_dropdown_option_id != ""
      && data.customer.pay_first_day_of_week_dropdown_option_id != ""
      && data.customer.pay_first_week_of_month_dropdown_option_id != null
      && data.customer.pay_first_day_of_week_dropdown_option_id != null) {
      setPayFrequencyValueOption('monthly-option2')
      setInitialPayFrequencyOption('monthly-option2');
      setValue('monthly_pay_frequency', [{ label: 'Pay Day of Month', value: 'monthly-option1' }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }].find(
        (option) => option.value == 'monthly-option2'
      ) ?? '')
    }

    // Set default pay frequency option for pay frequencies that don't have dependent fields
    if (data.customer.pay_frequency_dropdown_option_id == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_weekly')?.id) {
      setPayFrequencyValueOption('weekly-default')
      setInitialPayFrequencyOption('weekly-default');
    } else if (data.customer.pay_frequency_dropdown_option_id == dropdownOptions.find((option) => option?.code_key == 'pay_frequency_biweekly')?.id) {
      setPayFrequencyValueOption('biweekly-default')
      setInitialPayFrequencyOption('biweekly-default');
    }

    setValue('pay_first_day_of_month_dropdown_option_id', dayOfMonthOptions.find(
      (option) => option.value == data.customer.pay_first_day_of_month_dropdown_option_id
    ) ?? '')

    setValue('pay_second_day_of_month_dropdown_option_id', dayOfMonthOptions.find(
      (option) => option.value == data.customer.pay_second_day_of_month_dropdown_option_id
    ) ?? '')

    setValue('pay_first_week_of_month_dropdown_option_id', weekOfMonthOptions.find(
      (option) => option.value == data.customer.pay_first_week_of_month_dropdown_option_id
    ) ?? '')

    setValue('pay_second_week_of_month_dropdown_option_id', weekOfMonthOptions.find(
      (option) => option.value == data.customer.pay_second_week_of_month_dropdown_option_id
    ) ?? '')

    setValue('pay_second_day_of_week_dropdown_option_id', dayOfWeekOptions.find(
      (option) => option.value == data.customer.pay_second_day_of_week_dropdown_option_id
    ) ?? '')
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/refinance_schedule`)
    try {
      let response = []
      if (submissionType === 'patch') {
        let body = {}
        body.pay_frequency_dropdown_option_id = formData.pay_frequency_dropdown_option_id.value
        body.principal_amount = newLoanAmount ?? 0
        body.is_pay_before_holiday = formData.is_pay_before_holiday.value
        body.apr = formData.apr ?? 0
        body.program_id = formData.program_id.value
        body.principal_discount = formData.principal_discount
        body.interest_discount = formData.interest_discount
        body.additional_funds = formData.additional_funds ?? 0
        if (isCashOut) {
          let fundingMethodId = watchLoanFundingMethod?.value;
          if (!fundingMethodId && refinanceInfo?.loan_funding_method) {
            const fundingOptions = dropdownOptions.filter(
              (opt) => opt.code_key === refinanceInfo.loan_funding_method
            );
            if (fundingOptions.length > 0) {
              fundingMethodId = fundingOptions[0].id;
            }
          }
          if (fundingMethodId) {
            body.loan_funding_method_dropdown_option_id = fundingMethodId;
          }
        }

        // For cash out refinance, set the refinance reason to cash out
        if (isCashOut) {
          const cashOutReasonOption = dropdownOptions.filter((option) => option?.code_key === 'refinance_reason_cashout')[0];
          if (cashOutReasonOption) {
            body.refinance_reason_dropdown_option_id = cashOutReasonOption.id;
          }
        }

        // Add effective_date from refinanceInfo
        body.effective_date = refinanceInfo?.effective_date;

        if (formData.pay_frequency_dropdown_option_id.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_weekly')[0]?.id) {
          body.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value
        } else if (formData.pay_frequency_dropdown_option_id.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_biweekly')[0]?.id) {
          body.next_pay_date = formData.next_pay_date
        } else if (formData.pay_frequency_dropdown_option_id.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_semimonthly')[0]?.id) {
          if (payFrequencyValueOption == 'semimonthly-option1') {
            body.pay_first_day_of_month_dropdown_option_id = formData.pay_first_day_of_month_dropdown_option_id.value
            body.pay_second_day_of_month_dropdown_option_id = formData.pay_second_day_of_month_dropdown_option_id.value
          } else if (payFrequencyValueOption == 'semimonthly-option2') {
            body.pay_first_week_of_month_dropdown_option_id = formData.pay_first_week_of_month_dropdown_option_id.value
            body.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value
            body.pay_second_week_of_month_dropdown_option_id = formData.pay_second_week_of_month_dropdown_option_id.value
            body.pay_second_day_of_week_dropdown_option_id = formData.pay_second_day_of_week_dropdown_option_id.value
          }
        } else if (formData.pay_frequency_dropdown_option_id.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_monthly')[0]?.id) {
          if (payFrequencyValueOption == 'monthly-option1') {
            body.pay_first_day_of_month_dropdown_option_id = formData.pay_first_day_of_month_dropdown_option_id.value
          } else if (payFrequencyValueOption == 'monthly-option2') {
            body.pay_first_week_of_month_dropdown_option_id = formData.pay_first_week_of_month_dropdown_option_id.value
            body.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value
          }
        }

        response = await request.modify(body)

        if (response.status === 200 || response.status === 201) {
          setScheduleData(response?.data?.data)
          setRequiresManagerApproval(response?.data?.requires_manager_approval)
          setSaving(false)
          // After generating the schedule, always show the schedule for review (step 1)
          setCurrentStep(1)
          // Capture current form values when schedule is generated
          const currentFormValues = {
            principal_discount: watchPrincipalDiscount ?? 0,
            interest_discount: watchInterestDiscount ?? 0,
            additional_funds: watchAdditionalFunds ?? 0,
            pay_frequency_dropdown_option_id: watchPayFrequencyDropdownOptionId?.value,
            program_id: watchProgramId?.value,
            apr: watchApr ?? 0,
            is_pay_before_holiday: watchIsPayBeforeHoliday?.value,
            pay_first_day_of_week_dropdown_option_id: watch('pay_first_day_of_week_dropdown_option_id')?.value,
            pay_second_day_of_week_dropdown_option_id: watch('pay_second_day_of_week_dropdown_option_id')?.value,
            pay_first_week_of_month_dropdown_option_id: watch('pay_first_week_of_month_dropdown_option_id')?.value,
            pay_second_week_of_month_dropdown_option_id: watch('pay_second_day_of_week_dropdown_option_id')?.value,
            pay_first_day_of_month_dropdown_option_id: watch('pay_first_day_of_month_dropdown_option_id')?.value,
            pay_second_day_of_month_dropdown_option_id: watch('pay_second_day_of_month_dropdown_option_id')?.value,
            next_pay_date: watch('next_pay_date') ?? null,
            semi_monthly_pay_frequency: watch('semi_monthly_pay_frequency')?.value,
            monthly_pay_frequency: watch('monthly_pay_frequency')?.value,
          };
          setScheduleFormValues(currentFormValues);
          dispatch(setNotification({ type: 'success', body: '' }))
        } else {
          setCurrentStep(0)
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } else {
        let body = {}
        body.apr = formData.apr ?? 0
        body.program_id = formData.program_id.value
        body.principal_discount = formData.principal_discount ?? 0
        body.interest_discount = formData.interest_discount ?? 0
        body.additional_funds = formData.additional_funds ?? 0

        response = await request.post(body)

        if (response.status === 200 || response.status === 201) {
          setCurrentStep(2)
          setRefinanceRequestInfo(response?.data?.data)
          setRequiresManagerApproval(response?.data?.data?.requires_manager_approval)
          setSaving(false)
          dispatch(setNotification({ type: 'success', body: '' }))
        } else {
          setCurrentStep(1)
          setRefinanceRequestInfo({})
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      }
    } catch (error) {
      if ([403, 412, 422].includes(error?.response?.status)) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      } else {
        setCurrentStep(0) // reset to first step
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  };

  /*
  Collection Refinance Specific:
  Function to get refinance rates based on desired amount and pay frequency
  Validates input before making API call
  On success, updates collectionRefiRates and moves to step 1 (review offers)
  On failure, shows error notification
   */
  const getRefiRates = async (e) => {
    e.preventDefault();
    if (isCollectionRefinance) {
      if (watchDesiredAmount < collectionRefiMinMax?.min || watchDesiredAmount > collectionRefiMinMax?.max) {
        dispatch(setNotification({ type: 'error', body: `Desired amount must be between ${collectionRefiMinMax?.min} and ${collectionRefiMinMax?.max}` }));
        return;
      } else if (!watchPayFrequencyDropdownOptionId.value) {
        dispatch(setNotification({ type: 'error', body: `Pay frequency is required` }));
        return;
      }

      setSaving(true);
      let params = {};
      params.pay_frequency_dropdown_option_id = watchPayFrequencyDropdownOptionId.value;
      params.target_amount = watchDesiredAmount;
      params.first_payment_date = collectionsFirstPaymentDate ? format(collectionsFirstPaymentDate, 'yyyy-MM-dd') : refinanceInfo?.min_first_payment_date;
      params.is_pay_before_holiday = watchIsPayBeforeHoliday?.value;

      // Build URL with query parameters
      let url = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/collections_refi_rates`;
      url += buildQueryString(params)

      setOfferSelection(null);
      setCollectionRefiRates([]);
      setScheduleData(null);
      setDisableUpdateOfferButton(false);

      request.setEndpoint(url);
      try {
        const response = await request.get();
        if (response.status === 200 || response.status === 201) {
          setCollectionRefiRates(response?.data?.data || []);
          if (response?.data?.data?.length > 0) {
            setOfferSelection(0)
            setScheduleData(response?.data?.data[0] || []);
            setDisableUpdateOfferButton(true);
          } else {
            setOfferSelection(null);
          }
          setCurrentStep(1);
          setSaving(false);
        } else {
          setCollectionRefiRates([]);
          setOfferSelection(null);
          setCurrentStep(1);
          setSaving(false);
          setDisableUpdateOfferButton(false);
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
        }
      } catch (error) {
        setCollectionRefiRates([]);
        setOfferSelection(null);
        setCurrentStep(1);
        setSaving(false);
        setDisableUpdateOfferButton(false);
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme;
        dispatch(setNotification({ type: 'error', body: errorMessage }));
      }
    }
  };

  const sendCollectionRefinanceForSignature = async (e) => {
    e.preventDefault();
    if (isCollectionRefinance) {
      setIsSendingForSignature(true);
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/confirm_collections_refinance`;
      let body = {};
      body.guid = collectionRefiRates[offerSelection]?.guid;
      body.is_pay_before_holiday = watchIsPayBeforeHoliday?.value;
      request.setEndpoint(endpoint)
      try {
        const response = await request.post(body) // This should be GET method as it was before
        if (response.status === 200 || response.status === 201) {
          setRefinanceRequestInfo(response?.data?.data)
          setCurrentStep(3);
        } else {
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
        setIsSendingForSignature(false);
      } catch (error) {
        setIsSendingForSignature(false);
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  }

  const getManagerApprovalStatus = async () => {
    let managerApprovalId = refinanceRequestInfo?.manager_approval_id
    if (managerApprovalId) {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/manager_approval/${managerApprovalId}/refinance-status`;
      request.setEndpoint(endpoint)
    } else {
      return;
    }
    try {
      const response = await request.get()
      if (response.status === 200 || response.status === 201) {
        setManagerApprovalStatusResponse(response?.data?.data)
        setScheduleData(response?.data?.data?.updated_schedule)
        setManagerApprovalStatusInfo(dropdownOptions.find((x) => x?.id == response?.data?.data?.mgr_approval_status_dropdown_option_id))
      } else {
        setManagerApprovalStatusResponse({})
        setManagerApprovalStatusInfo({})
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setManagerApprovalStatusResponse({})
      setManagerApprovalStatusInfo({})
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const getLoanAgreementData = async () => {
    try {
      let leadId = refinanceRequestInfo?.lead_id
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/lead/${leadId}/loan_agreement_info`)
      const response = await request.get()
      setLoanAgreementData(response?.data)
    } catch (error) {
      if (error?.response?.status === 404) {
        navi('/404')
      } else {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      }
    }
  }

  const getRefinanceType = () => {
    if (isCashOut) return 'Cash-Out';
    if (isCollectionRefinance) return 'Collections';
    return 'No-Cash Out';
  };

  const cancelCollectionRefinance = () => {
    if (!isCollectionRefinance) return false;
    setOfferSelection(null);
    setCollectionRefiRates([]);
    setScheduleData(null);
    setDisableUpdateOfferButton(false);
    setDisplayFrequencyWarning(false);
  };

  const getUpdatedTermsForCashout = async () => {
    setIsUpdatingTerms(true);
    setScheduleData(null);
    setScheduleFormValues({});
    setCurrentStep(0);
    // Gather current form values
    const formValues = getValues();
    const currentProgram = formValues.program_id;
    const currentApr = formValues.apr;
    const currentAdditionalFunds = formValues.additional_funds;

    // Build body for refinance_info
    let body = {
      is_cash_out: true,
      pay_frequency_dropdown_option_id: formValues.pay_frequency_dropdown_option_id?.value,
    };
    // Add pay frequency dependent fields
    if (formValues.pay_frequency_dropdown_option_id?.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_weekly')?.id) {
      body.pay_first_day_of_week_dropdown_option_id = formValues.pay_first_day_of_week_dropdown_option_id?.value;
    } else if (formValues.pay_frequency_dropdown_option_id?.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_biweekly')?.id) {
      body.next_pay_date = formValues.next_pay_date;
    } else if (formValues.pay_frequency_dropdown_option_id?.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_semimonthly')?.id) {
      if (payFrequencyValueOption === 'semimonthly-option1') {
        body.pay_first_day_of_month_dropdown_option_id = formValues.pay_first_day_of_month_dropdown_option_id?.value;
        body.pay_second_day_of_month_dropdown_option_id = formValues.pay_second_day_of_month_dropdown_option_id?.value;
      } else if (payFrequencyValueOption === 'semimonthly-option2') {
        body.pay_first_week_of_month_dropdown_option_id = formValues.pay_first_week_of_month_dropdown_option_id?.value;
        body.pay_first_day_of_week_dropdown_option_id = formValues.pay_first_day_of_week_dropdown_option_id?.value;
        body.pay_second_week_of_month_dropdown_option_id = formValues.pay_second_week_of_month_dropdown_option_id?.value;
        body.pay_second_day_of_week_dropdown_option_id = formValues.pay_second_day_of_week_dropdown_option_id?.value;
      }
    } else if (formValues.pay_frequency_dropdown_option_id?.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_monthly')?.id) {
      if (payFrequencyValueOption === 'monthly-option1') {
        body.pay_first_day_of_month_dropdown_option_id = formValues.pay_first_day_of_month_dropdown_option_id?.value;
      } else if (payFrequencyValueOption === 'monthly-option2') {
        body.pay_first_week_of_month_dropdown_option_id = formValues.pay_first_week_of_month_dropdown_option_id?.value;
        body.pay_first_day_of_week_dropdown_option_id = formValues.pay_first_day_of_week_dropdown_option_id?.value;
      }
    }
    body.program_id = formValues.program_id?.value;
    body.is_pay_before_holiday = formValues.is_pay_before_holiday?.value;

    // Get updated refinance info
    await getRefinanceInfo(customerId, body);

    // If there was a schedule, go back to step 1 but preserve form state
    if (scheduleData) {
      setCurrentStep(1);
      // Clear schedule data after setting step to avoid showing old data
      setScheduleData(null);
    }

    // Small delay to ensure state updates have propagated
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });

    // Only restore program if it's still available in the updated programs list
    if (currentProgram && availablePrograms.some((p) => p.id === currentProgram.value)) {
      setValue('program_id', currentProgram);

      // After restoring the program, update the APR limits
      const restoredProgram = availablePrograms.find((p) => p.id === currentProgram.value);
      if (restoredProgram) {
        const activeLoanApr = refinanceInfo?.active_loan?.apr ?? null;
        const programMaxApr = restoredProgram.max_apr ?? null;

        let newMinApr = restoredProgram.min_apr ?? null;
        let newMaxApr = null;

        if (activeLoanApr !== null && programMaxApr !== null) {
          newMaxApr = Math.min(activeLoanApr, programMaxApr);
        } else if (activeLoanApr !== null) {
          newMaxApr = activeLoanApr;
        } else if (programMaxApr !== null) {
          newMaxApr = programMaxApr;
        }

        setMinApr(newMinApr);
        setMaxApr(newMaxApr);
        setMaxPrincipal(restoredProgram.max_principal);
        setMinPrincipal(restoredProgram.min_principal);
        setNumOfInstallments(restoredProgram.number_of_installments);
      }
    }

    if (currentApr !== undefined && currentApr !== null) {
      setValue('apr', currentApr);
    } else if (maxApr !== null && maxApr !== undefined) {
      // If no prior APR value, default to the current max APR cap
      setValue('apr', maxApr);
    }

    if (currentAdditionalFunds !== undefined && currentAdditionalFunds !== null) {
      setValue('additional_funds', currentAdditionalFunds);
    }

    // Recalculate new loan amount after restoring form values
    const currentPrincipalDiscount = getValues('principal_discount') ?? 0;
    const currentInterestDiscount = getValues('interest_discount') ?? 0;
    const currentAdditionalFundsValue = getValues('additional_funds') ?? 0;

    if (isCashOut) {
      const newAmount = +refinanceInfo.payoff_amount + +currentAdditionalFundsValue - +currentPrincipalDiscount - +currentInterestDiscount;
      setNewLoanAmount(newAmount);
    } else {
      const newAmount = +refinanceInfo.payoff_amount - +currentPrincipalDiscount - +currentInterestDiscount;
      setNewLoanAmount(newAmount);
    }

    setIsUpdatingTerms(false);
  }

  return (
    <LayoutSidebar
      contentTitle={<>Refinance - {getRefinanceType()} {requiresManagerApproval && <ManagerApprovalIcons type="mgr_approval_status_required" />}</>}
      actionButtons={(
        <button
          type="button"
          className="button has-background-grey has-text-white is-small header-button"
          onClick={() => {
            navi(`/customer/${customerId}`)
          }}
        >
          <span className="mx-3">Customer Details</span>
        </button>
      )}
      content={(
        <div className="lox-detail mt-1 mx-1">
          {(requiresManagerApproval && currentStep === 2) && (
            <ManagerApprovalStatus managerApprovalStatusInfo={managerApprovalStatusInfo} setCurrentStep={setCurrentStep} managerApprovalStatusResponse={managerApprovalStatusResponse} />
          )}
          {(!requiresManagerApproval && currentStep === 2) && (
            <ManagerApprovalStatus managerApprovalStatusInfo={managerApprovalStatusInfo} setCurrentStep={setCurrentStep} managerApprovalStatusResponse={managerApprovalStatusResponse} managerApprovalStatusNotRequired />
          )}
          <div className="columns mb-0">
            <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
              <div className="title is-4 has-text-primary is-flex">{stepDetails[currentStep]?.title}</div>
              {(currentStep !== 3 && requiresManagerApproval) && <p className="subtitle is-6">{stepDetails[currentStep]?.description}</p>}
            </div>
          </div>

          {!refinanceInfo && (
            <Loading />
          )}

          {refinanceInfo && refinanceErrorMessage !== "" && (
            <Instruction content={refinanceErrorMessage} />
          )}

          {refinanceInfo && refinanceErrorMessage == "" && (
            <form onSubmit={handleSubmit(onSubmit)} id="loan_refinance" data-testid="loan-refinance-form">
              <div className="columns">
                {(currentStep == 0 || currentStep == 1) && (
                  <div className="column">
                    <div className="columns mb-0 is-flex-direction-column">
                      <div className="column">
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isCollectionRefinance ? (
                          <CollectionRefinanceFields
                            control={control}
                            refinanceInfo={refinanceInfo}
                            payFrequencyOptions={payFrequencyOptions}
                            dropdownOptions={dropdownOptions}
                            today={today}
                            refinanceReasonOptions={refinanceReasonOptions}
                            watchDesiredAmount={watchDesiredAmount}
                            setPayFrequencyValue={setPayFrequencyValue}
                            setPayFrequencyValueOption={setPayFrequencyValueOption}
                            collectionRefiMinMax={collectionRefiMinMax}
                            collectionRefiRates={collectionRefiRates}
                            getCollectionRefiMinMax={getCollectionRefiMinMax}
                            loanId={loanId}
                            isMaxMinProcessing={isMaxMinProcessing}
                            setOfferSelection={setOfferSelection}
                            offerSelection={offerSelection}
                            collectionsFirstPaymentDate={collectionsFirstPaymentDate}
                            setCollectionsFirstPaymentDate={setCollectionsFirstPaymentDate}
                            cancelCollectionRefinance={cancelCollectionRefinance}
                            displayFrequencyWarning={displayFrequencyWarning}
                          />
                        ) : isCashOut ? (
                          <CashOutRefinanceFields
                            control={control}
                            refinanceInfo={refinanceInfo}
                            newLoanAmount={newLoanAmount}
                            payFrequencyOptions={payFrequencyOptions}
                            dayOfWeekOptions={dayOfWeekOptions}
                            weekOfMonthOptions={weekOfMonthOptions}
                            dayOfMonthOptions={dayOfMonthOptions}
                            availablePrograms={availablePrograms}
                            setPayFrequencyValue={setPayFrequencyValue}
                            setPayFrequencyValueOption={setPayFrequencyValueOption}
                            handleProgramChange={handleProgramChange}
                            minPrincipal={minPrincipal}
                            maxPrincipal={maxPrincipal}
                            minApr={minApr}
                            maxApr={maxApr}
                            numOfInstallments={numOfInstallments}
                            dropdownOptions={dropdownOptions}
                            nextDay={nextDay}
                            today={today}
                            watch={watch}
                            payFrequencyValue={payFrequencyValue}
                            payFrequencyValueOption={payFrequencyValueOption}
                          />
                        ) : (
                          <>
                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Refinance Reason:</label>
                              <div className="control">
                                <span>{refinanceInfo?.refinance_reason || 'N/A'}</span>
                              </div>
                            </div>
                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="email_address">Old loan&apos;s
                                payoff
                                amount:</label>
                              <div className="control"><CurrencyFormat value={refinanceInfo?.payoff_amount} /></div>
                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="principal_discount">Principal
                                Discount
                                Amount:</label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="principal_discount"
                                  control={control}
                                  defaultValue={0}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="number"
                                      className="input is-small"
                                      min={0}
                                      max={refinanceInfo?.max_principal_discount}
                                      step="0.01"
                                    />
                                  )}
                                />
                                <div><small>Max: <CurrencyFormat value={refinanceInfo?.max_principal_discount} /></small>
                                </div>
                              </div>
                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="interest_discount">Interest
                                Discount
                                Amount:</label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="interest_discount"
                                  control={control}
                                  defaultValue={0}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="number"
                                      className="input is-small"
                                      min={0}
                                      max={refinanceInfo?.max_interest_discount}
                                      step="0.01"
                                    />
                                  )}
                                />
                                <div><small>Max: <CurrencyFormat value={refinanceInfo?.max_interest_discount} /></small>
                                </div>
                              </div>
                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`}>New Loan
                                Amount:</label>
                              <div className="control"><CurrencyFormat value={newLoanAmount} /></div>
                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`}>New Loan Effective
                                Date:</label>
                              <div className="control"><DateFormat dateString={today} /></div>
                            </div>

                            <div className="field is-horizontal">
                              <label
                                className={`refinance-loan-modal-label`}
                                htmlFor="pay_frequency_dropdown_option_id"
                              >Pay Frequency:<span
                                className="has-text-danger"
                              >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_frequency_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <LoxSelect
                                      {...field}
                                      testId="loan-refinance-pay-frequency"
                                      classNames={reactSelectClassNames()}
                                      options={payFrequencyOptions}
                                      isSearchable={false}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                      onChange={(val, actionMeta) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                        setPayFrequencyValue(val?.label)
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${payFrequencyValue == 'Semi-Monthly' ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Semi Monthly Option:<span className="has-text-danger">*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="semi_monthly_pay_frequency"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={[{
                                        label: 'Specific Days',
                                        value: 'semimonthly-option1',
                                      }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }]}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                        setPayFrequencyValueOption(val?.value)
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div className={`field is-horizontal ${payFrequencyValue == 'Monthly' ? '' : 'is-hidden'}`}>
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Monthly Option:<span
                                className="has-text-danger"
                              >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="monthly_pay_frequency"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={[{
                                        label: 'Pay Day of Month',
                                        value: 'monthly-option1',
                                      }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }]}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                        setPayFrequencyValueOption(val?.value)
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${(payFrequencyValue == 'Weekly' || (payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') || (payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2')) ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">First Day of
                                Week:<span
                                  className="has-text-danger"
                                >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_first_day_of_week_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={dayOfWeekOptions}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${payFrequencyValue == 'Bi-Weekly' ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Next Payment Day:<span
                                className="has-text-danger"
                              >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="next_pay_date"
                                  control={control}
                                  defaultValue={null}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      data-testid="loan-refinance-next-pay-date"
                                      type="date"
                                      className="input is-small"
                                      min={nextDay}
                                      disabled={payFrequencyValue !== 'Bi-Weekly'}
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Second Day of
                                Week:<span
                                  className="has-text-danger"
                                >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_second_day_of_week_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={dayOfWeekOptions}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${((payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') || (payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option2')) ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">First Week of
                                Month:<span
                                  className="has-text-danger"
                                >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_first_week_of_month_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={weekOfMonthOptions}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option2') ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Second Week of
                                Month:<span
                                  className="has-text-danger"
                                >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_second_week_of_month_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={weekOfMonthOptions}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${((payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option1') || (payFrequencyValue == 'Monthly' && payFrequencyValueOption == 'monthly-option1')) ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">First Day of
                                Month:<span
                                  className="has-text-danger"
                                >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_first_day_of_month_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={dayOfMonthOptions}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div
                              className={`field is-horizontal ${(payFrequencyValue == 'Semi-Monthly' && payFrequencyValueOption == 'semimonthly-option1') ? '' : 'is-hidden'}`}
                            >
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Second Day of
                                Month:<span
                                  className="has-text-danger"
                                >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="pay_second_day_of_month_dropdown_option_id"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={dayOfMonthOptions}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Holiday or Weekend
                                Payday:<span className="has-text-danger">*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="is_pay_before_holiday"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      classNames={reactSelectClassNames()}
                                      options={[{ label: 'Before', value: 1 }, { label: 'After', value: 0 }]}
                                      isSearchable={false}
                                      onChange={(val) => {
                                        field.onChange({ label: val?.label, value: val?.value })
                                      }}
                                      styles={{
                                        container: (base) => ({ ...base, width: '100%' }),
                                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                      }}
                                      menuPosition="fixed"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">Program:<span
                                className="has-text-danger"
                              >*</span></label>
                              <div className="field">
                                <div className="control refinance-loan-modal-fields">
                                  <div style={{ width: '100%' }}>
                                    <Controller
                                      name="program_id"
                                      control={control}
                                      defaultValue=""
                                      render={({ field }) => (
                                        <LoxSelect
                                          {...field}
                                          testId="loan-refinance-program"
                                          classNames={reactSelectClassNames()}
                                          options={availablePrograms?.map((o) => ({
                                            label: o.name,
                                            value: o.id,
                                            pay_frequency_dropdown_option_id: o.pay_frequency_dropdown_option_id,
                                          }))}
                                          isSearchable={false}
                                          onChange={(val) => {
                                            handleProgramChange(field, val)
                                          }}
                                          styles={{
                                            container: (base) => ({ ...base, width: '100%' }),
                                            menu: ({ ...css }) => ({ ...css, width: '100%' }),
                                          }}
                                          menuPosition="fixed"
                                        />
                                      )}
                                    />
                                  </div>
                                </div>
                                {minPrincipal && maxPrincipal && (
                                  <p
                                    className="help"
                                  >{`Min Principal: $${minPrincipal} - Max Principal: $${maxPrincipal}`}</p>
                                )}
                                {minApr && maxApr &&
                                  <p className="help">{`Min APR: ${minApr}% - Max APR: ${maxApr}%`}</p>}
                                {numOfInstallments &&
                                  <p className="help">{`Number Of Installments: ${numOfInstallments}`}</p>}

                              </div>

                            </div>

                            <div className="field is-horizontal">
                              <label className={`refinance-loan-modal-label`} htmlFor="first_name">APR:<span
                                className="has-text-danger"
                              >*</span></label>
                              <div className="control refinance-loan-modal-fields">
                                <Controller
                                  name="apr"
                                  control={control}
                                  // Default APR is set when a program is selected; leave blank initially.
                                  defaultValue={maxApr ?? null}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="number"
                                      className="input is-small"
                                      min={minApr || undefined}
                                      max={maxApr || undefined}
                                      step="0.001"
                                    />
                                  )}
                                />
                                <div><small className={watchApr && ((minApr !== null && watchApr < minApr) || (maxApr !== null && watchApr > maxApr)) ? 'has-text-danger' : ''}>
                                  Min: {minApr || 'N/A'}% - Max: {maxApr || 'N/A'}%
                                </small></div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="column">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {isCollectionRefinance && refinanceInfo?.collection_refi_lead_id && (currentStep === 0 || currentStep === 1) && (
                            <button
                              type="button"
                              className="button is-link is-small px-2"
                              disabled={saving || isMaxMinProcessing || disableUpdateOfferButton}
                              onClick={async (e) => {
                                getRefiRates(e);
                              }}
                            >
                              {/*eslint-disable-next-line no-nested-ternary*/}
                              {saving ? (<><Loading size="is-size-7" /> Processing...</>) : currentStep === 0 ? 'Submit' : 'Update Offer'}
                            </button>
                          )}

                          {/* Display the Generate Schedule button only when:
                            - This is not a collection refinance, AND
                            - If it is a cashout refinance, refinanceInfo?.lead_id must have a value. */}
                          {!isCollectionRefinance && (!isCashOut || (isCashOut && refinanceInfo?.lead_id)) && (
                            <button
                              type="submit"
                              data-testid="loan-refinance-generate-schedule"
                              className="button is-link is-small px-2"
                              disabled={
                                saving ||
                                !isFormValid() ||
                                (
                                  isCashOut && (payFrequencyValue !== initialPayFrequency || payFrequencyValueOption !== initialPayFrequencyOption)
                                )
                              }
                              onClick={() => setSubmissionType('patch')}
                            >{(saving && submissionType == 'patch') ? (
                              <><Loading size="is-size-7" /> Generating</>
                            ) : 'Generate Schedule'}</button>
                          )}

                          {isCashOut && refinanceInfo?.lead_id != null && (
                            <span style={{ marginLeft: '8px', position: 'relative', display: 'inline-block' }}>
                              <button
                                type="button"
                                className="button is-link is-small px-2 ml-2"
                                style={{ marginLeft: 'auto' }}
                                disabled={
                                  !isFormValid() ||
                                  (
                                    (payFrequencyValue === initialPayFrequency && payFrequencyValueOption === initialPayFrequencyOption)
                                  ) || isUpdatingTerms
                                }
                                onClick={() => getUpdatedTermsForCashout()}
                              >
                                {isUpdatingTerms ? (
                                  <>
                                    <Loading size="is-size-7" /> Updating...
                                  </>
                                ) : (
                                  'Update Terms'
                                )}
                                {(!isFormValid() || (payFrequencyValue === initialPayFrequency && payFrequencyValueOption === initialPayFrequencyOption)) && (
                                  <span
                                    className={
                                      'lox-no-border ml-1 lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-left has-tooltip-primary'
                                    }
                                    data-tooltip="You must update terms when changing the pay frequency."
                                    style={{ pointerEvents: 'auto', verticalAlign: 'middle' }}
                                  >
                                    <FontAwesomeIcon icon={faCircleInfo} className="has-text-link" />
                                  </span>
                                )}
                              </button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {(currentStep == 0 || currentStep == 1 || currentStep == 2) && isCollectionRefinance && offerSelection != null && (
                  <CollectionRefinanceSchedule
                    scheduleData={scheduleData}
                    setCurrentStep={setCurrentStep}
                    sendCollectionRefinanceForSignature={sendCollectionRefinanceForSignature}
                    isSendingForSignature={isSendingForSignature}
                    cancelCollectionRefinance={cancelCollectionRefinance}
                  />
                )}
                {(currentStep == 1 || currentStep == 2) && scheduleData && !isCollectionRefinance && (
                  <div className="column">
                    <div className="columns mb-0 is-flex-direction-column">
                      <div className="column">
                        <div className="columns is-multiline">
                          <div className="column is-half">
                            <p><strong>Total Amount:</strong> <CurrencyFormat value={scheduleData?.total_amount} />
                            </p>
                            <p><strong>Total Interest:</strong> <CurrencyFormat
                              value={scheduleData?.total_interest}
                            /></p>
                            <p><strong>Loan Due Date:</strong> <DateFormat dateString={scheduleData?.loan_due_date} />
                            </p>
                          </div>
                          <div className="column is-half">
                            <p><strong>Effective Date:</strong> <DateFormat
                              dateString={scheduleData?.effective_date}
                            /></p>
                            <p><strong>APR:</strong> {formatApr(scheduleData?.apr)}%</p>
                            <p><strong>Interest Rate:</strong> {parseFloat(scheduleData?.interest_rate).toFixed(2)}%</p>
                          </div>
                        </div>
                        <div className="table-container">
                          <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth is-grey mb-3">
                            <thead>
                              <tr>
                                <th colSpan="5" className="is-selected has-text-left lox-payment-schedule-color">Payment Schedule</th>
                              </tr>
                              <tr>
                                <th className="is-selected has-text-centered lox-payment-schedule-color">
                                  #
                                </th>
                                <th className="is-selected has-text-centered lox-payment-schedule-color">
                                  Date
                                </th>
                                <th className="is-selected has-text-centered lox-payment-schedule-color">
                                  Principal $
                                </th>
                                <th className="is-selected has-text-centered lox-payment-schedule-color">
                                  Interest $
                                </th>
                                <th className="is-selected has-text-centered lox-payment-schedule-color">
                                  Total $$$
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {scheduleData?.schedule?.map((item, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={index}>
                                  <td className="has-text-centered" style={{ whiteSpace: 'nowrap' }}>{index + 1}</td>
                                  <td
                                    className="has-text-left"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    <DateFormat dateString={item.date} />
                                  </td>
                                  <td
                                    className="has-text-left"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    <CurrencyFormat value={+item.principal_amount} />
                                  </td>
                                  <td
                                    className="has-text-left"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    <CurrencyFormat value={+item.payment - +item.principal_amount} />
                                  </td>
                                  <td
                                    className="has-text-left"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    <CurrencyFormat value={+item.payment} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <thead>
                              <tr>
                                <th colSpan="2" className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}>TOTAL PLAN</th>
                                <th className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}>
                                  <CurrencyFormat value={scheduleData?.schedule?.reduce((total, item) => total + +item.principal_amount, 0)} />
                                </th>
                                <th className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}>
                                  <CurrencyFormat value={scheduleData?.schedule?.reduce((total, item) => total + (+item.payment - +item.principal_amount), 0)} />
                                </th>
                                <th className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}>
                                  <CurrencyFormat value={scheduleData?.schedule?.reduce((total, item) => total + +item.payment, 0)} />
                                </th>
                              </tr>
                            </thead>
                          </table>
                        </div>
                        <div className="lox-form__buttons-wrapper has-text-right">
                          {currentStep === 1 && (
                            <>
                              <button
                                type="button"
                                className="button is-light is-small mr-2"
                                onClick={() => setCurrentStep(0)}
                              >
                                Cancel
                              </button>
                              {!requiresManagerApproval ? (
                                <button
                                  type="submit"
                                  data-testid="loan-refinance-next"
                                  className="button is-link is-small px-2"
                                  disabled={saving || scheduleData == null || hasFormChanges()}
                                  onClick={async (e) => {
                                    setSubmissionType('post');
                                    // For cash out, skip manager approval and go to loan agreement (step 3)
                                    if (isCashOut) {
                                      e.preventDefault();
                                      setSaving(true);
                                      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/refinance_schedule`);
                                      let body = {};
                                      body.apr = watchApr ?? 0;
                                      body.program_id = watchProgramId.value;
                                      body.principal_discount = watchPrincipalDiscount ?? 0;
                                      body.interest_discount = watchInterestDiscount ?? 0;
                                      body.additional_funds = watchAdditionalFunds ?? 0;

                                      // For cash out, set the refinance reason to cash out
                                      const cashOutReasonOption = dropdownOptions.filter((option) => option?.code_key === 'refinance_reason_cashout')[0];
                                      if (cashOutReasonOption) {
                                        body.refinance_reason_dropdown_option_id = cashOutReasonOption.id;
                                      }
                                      // Add effective_date from refinanceInfo
                                      body.effective_date = refinanceInfo?.effective_date;
                                      // Add lead_id from refinanceInfo
                                      body.lead_id = refinanceInfo?.lead_id;
                                      // Always send funding method for cash out
                                      if (isCashOut) {
                                        let fundingMethodId = watchLoanFundingMethod?.value;
                                        let fundingOptions = [];
                                        if (!fundingMethodId && refinanceInfo?.loan_funding_method) {
                                          fundingOptions = dropdownOptions.filter(
                                            (opt) => opt.code_key === refinanceInfo.loan_funding_method
                                          );
                                          if (fundingOptions.length > 0) {
                                            fundingMethodId = fundingOptions[0].id;
                                          }
                                        }
                                        if (fundingMethodId) {
                                          body.loan_funding_method_dropdown_option_id = fundingMethodId;
                                        }
                                      }
                                      // Add pay frequency dependent fields
                                      if (watchPayFrequencyDropdownOptionId.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_weekly')[0]?.id) {
                                        body.pay_first_day_of_week_dropdown_option_id = watch('pay_first_day_of_week_dropdown_option_id')?.value;
                                      } else if (watchPayFrequencyDropdownOptionId.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_biweekly')[0]?.id) {
                                        body.next_pay_date = watch('next_pay_date');
                                      } else if (watchPayFrequencyDropdownOptionId.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_semimonthly')[0]?.id) {
                                        if (payFrequencyValueOption == 'semimonthly-option1') {
                                          body.pay_first_day_of_month_dropdown_option_id = watch('pay_first_day_of_month_dropdown_option_id')?.value;
                                          body.pay_second_day_of_month_dropdown_option_id = watch('pay_second_day_of_month_dropdown_option_id')?.value;
                                        } else if (payFrequencyValueOption == 'semimonthly-option2') {
                                          body.pay_first_week_of_month_dropdown_option_id = watch('pay_first_week_of_month_dropdown_option_id')?.value;
                                          body.pay_first_day_of_week_dropdown_option_id = watch('pay_first_day_of_week_dropdown_option_id')?.value;
                                          body.pay_second_week_of_month_dropdown_option_id = watch('pay_second_week_of_month_dropdown_option_id')?.value;
                                          body.pay_second_day_of_week_dropdown_option_id = watch('pay_second_day_of_week_dropdown_option_id')?.value;
                                        }
                                      } else if (watchPayFrequencyDropdownOptionId.value == dropdownOptions.filter((option) => option?.code_key == 'pay_frequency_monthly')[0]?.id) {
                                        if (payFrequencyValueOption == 'monthly-option1') {
                                          body.pay_first_day_of_month_dropdown_option_id = watch('pay_first_day_of_month_dropdown_option_id')?.value;
                                        } else if (payFrequencyValueOption == 'monthly-option2') {
                                          body.pay_first_week_of_month_dropdown_option_id = watch('pay_first_week_of_month_dropdown_option_id')?.value;
                                          body.pay_first_day_of_week_dropdown_option_id = watch('pay_first_day_of_week_dropdown_option_id')?.value;
                                        }
                                      }
                                      try {
                                        const response = await request.post(body);
                                        if (response.status === 200 || response.status === 201) {
                                          setRefinanceRequestInfo(response?.data?.data);
                                          setSaving(false);
                                          setCurrentStep(3);
                                        } else {
                                          setSaving(false);
                                          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
                                        }
                                      } catch (error) {
                                        setSaving(false);
                                        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme;
                                        dispatch(setNotification({ type: 'error', body: errorMessage }));
                                      }
                                    }
                                  }}
                                >
                                  {saving && submissionType === 'post' ? (
                                    <>
                                      <Loading size="is-size-7" /> Sending to Next Page
                                    </>
                                  ) : (
                                    <>
                                      Next
                                      {hasFormChanges() && (
                                        <span
                                          className={
                                            'lox-no-border ml-1 lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-left has-tooltip-primary'
                                          }
                                          data-tooltip="Form values have changed, please regenerate schedule"
                                          style={{ pointerEvents: 'auto', verticalAlign: 'middle' }}
                                        >
                                          <FontAwesomeIcon icon={faCircleInfo} className="has-text-link" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </button>
                              ) : (
                                <button
                                  type="submit"
                                  className="button is-link is-small px-2"
                                  disabled={saving || scheduleData == null || hasFormChanges()}
                                  onClick={() => setSubmissionType('post')}
                                >
                                  {saving && submissionType === 'post' ? (
                                    <>
                                      <Loading size="is-size-7" /> Sending For Manager Approval
                                    </>
                                  ) : 'Send For Manager Approval'}
                                </button>
                              )}
                            </>
                          )}
                          {(currentStep === 2 && (managerApprovalStatusInfo?.code_key === 'mgr_approval_status_approved' || !requiresManagerApproval)) && (
                            <>
                              <button
                                type="button"
                                className="button is-light is-small mr-2"
                                onClick={() => setCurrentStep(0)}
                              >
                                Cancel
                              </button>
                              <button type="button" className="button is-link is-small px-2" onClick={() => setCurrentStep(3)}>Send For Signature</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {currentStep == 3 && (
                  <div className="column">
                    <div className="columns mb-0 is-flex-direction-column">
                      <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
                        <div className="title is-4 has-text-primary is-flex">Important</div>
                        <p className="subtitle is-6">
                          - Customer must sign agreement by midnight tonight or we do void the new loan and leave the old loan in effect.<br />
                          - New loan will not be effective until we have cleared the cancellation period of the new loan, so if canceled, old loan remains effect.
                        </p>
                        <LoanAgreement
                          loanAgreementData={loanAgreementData}
                        />
                      </div>
                      <div className="column">
                        <div className="loan-signing-container">
                          <nav className="panel">
                            <p className="panel-heading">Agent Acknowledgement</p>
                            <div className="container p-2">
                              <div className="columns">
                                <div className="column is-full">
                                  <div className="field">
                                    <div className="control">
                                      <label className="checkbox">
                                        <input type="checkbox" onClick={() => setTermsAgreement(!termsAgreement)} /> I have read the Terms & Conditions to the Customer <span className="has-text-danger">*</span>
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="columns pt-2 mb-0">
                                <div className="column is-half">
                                  Loan Amount: <CurrencyFormat value={loanAgreementData?.principal_amount} />
                                </div>
                                <div className="column is-half">
                                  Bank Account Number: ######{refinanceInfo?.customer?.bank_account_number}
                                </div>
                              </div>
                              <div className="columns">
                                <div className="column is-half">
                                  APR: {formatApr(scheduleData?.apr)}%
                                </div>
                                <div className="column is-half">
                                  Pay in Full on First Payment Date: <CurrencyFormat value={+loanAgreementData?.principal_amount + (+scheduleData?.schedule?.[0]?.payment - +scheduleData?.schedule?.[0]?.principal_amount)} />
                                </div>
                              </div>

                              <div className="columns">
                                <div className="column is-full">
                                  <p>First Payment Details:</p>

                                  <div>
                                    {scheduleData?.schedule && (
                                      <div className="table-container mb-0 position-relative">
                                        <table
                                          className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth is-grey mb-3"
                                        >
                                          <thead>
                                            <tr>
                                              <th className="is-selected has-text-centered lox-payment-schedule-color">Date</th>
                                              <th className="is-selected has-text-centered lox-payment-schedule-color">Principal $</th>
                                              <th className="is-selected has-text-centered lox-payment-schedule-color">Interest $</th>
                                              <th className="is-selected has-text-centered lox-payment-schedule-color">Total $$$</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {scheduleData?.schedule?.[0] ? (
                                              <tr>
                                                <td className="has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                                                  <DateFormat dateString={scheduleData.schedule[0].date} />
                                                </td>
                                                <td className="has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                                                  <CurrencyFormat value={+scheduleData.schedule[0].principal_amount} />
                                                </td>
                                                <td className="has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                                                  <CurrencyFormat value={+scheduleData.schedule[0].payment - +scheduleData.schedule[0].principal_amount} />
                                                </td>
                                                <td className="has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                                                  <CurrencyFormat value={+scheduleData.schedule[0].payment} />
                                                </td>
                                              </tr>
                                            ) : null}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </nav>
                        </div>
                      </div>
                      <div className="column">
                        <div className="lox-form__buttons-wrapper has-text-right">
                          {!isCollectionRefinance && (
                            <button type="button" className="button is-light is-small mr-2" onClick={() => setCurrentStep(0)}>Cancel</button>
                          )}
                          <button type="button" className="button is-link is-small px-2" disabled={!termsAgreement} onClick={() => navi(`/customer/${params.id}`)}>Finish</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </form>
          )}
        </div>
      )}
    />
  )
}
